import React, { useState, useEffect, useMemo } from "react";
import api from "../utils/axiosInstance";
import { useModal } from "../context/ModalContext";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import {
  FaChartLine,
  FaPaperPlane,
  FaInbox,
  FaCheckCircle,
  FaBullhorn,
  FaDownload,
  FaClock,
  FaWhatsapp,
  FaShieldAlt,
  FaTimes,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaCalendarAlt,
} from "react-icons/fa";
import {
  Card, CardHeader, CardBody,
  PageHeader, Button, Spinner,
  TableWrapper, Table, Th, Td,
  StatCard, EmptyState,
} from "../components/ui";
import { useTheme } from "../context/ThemeContext";

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Filler, Tooltip, Legend
);

const RANGE_OPTIONS = [
  { label: "Last 7 days", value: 7 },
  { label: "Last 14 days", value: 14 },
  { label: "Last 30 days", value: 30 },
  { label: "Last 60 days", value: 60 },
  { label: "Last 90 days", value: 90 },
  { label: "Month to date", value: "mtd" },
  { label: "Year to date", value: "ytd" },
];

const DAYS_OF_WEEK = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

function DateRangePicker({ onApply, currentLabel }) {
  const [open, setOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const ref = React.useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handlePreset = (opt) => {
    setOpen(false);
    setShowCalendar(false);
    onApply({ preset: opt.value, label: opt.label });
  };

  const handleCustomClick = () => {
    setShowCalendar(true);
    setRangeStart(null);
    setRangeEnd(null);
  };

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const handleDayClick = (day) => {
    const clicked = new Date(calYear, calMonth, day);
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(clicked);
      setRangeEnd(null);
    } else {
      if (clicked < rangeStart) {
        setRangeEnd(rangeStart);
        setRangeStart(clicked);
      } else {
        setRangeEnd(clicked);
      }
    }
  };

  const isInRange = (day) => {
    const d = new Date(calYear, calMonth, day);
    const start = rangeStart;
    const end = rangeEnd || hoverDate;
    if (!start || !end) return false;
    const [lo, hi] = start <= end ? [start, end] : [end, start];
    return d >= lo && d <= hi;
  };

  const isStart = (day) => rangeStart && new Date(calYear, calMonth, day).toDateString() === rangeStart.toDateString();
  const isEnd = (day) => {
    const end = rangeEnd || hoverDate;
    return end && new Date(calYear, calMonth, day).toDateString() === end.toDateString();
  };
  const isToday = (day) => new Date(calYear, calMonth, day).toDateString() === new Date().toDateString();
  const isFuture = (day) => new Date(calYear, calMonth, day) > new Date();

  const handleApply = () => {
    if (!rangeStart || !rangeEnd) return;
    const fmt = (d) => d.toISOString().split("T")[0];
    const fromStr = fmt(rangeStart);
    const toStr = fmt(rangeEnd);
    const label = `${rangeStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${rangeEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
    onApply({ from: fromStr, to: toStr, label });
    setOpen(false);
    setShowCalendar(false);
  };

  const handleClear = () => {
    setRangeStart(null);
    setRangeEnd(null);
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else setCalMonth(calMonth - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else setCalMonth(calMonth + 1);
  };

  const daysInMonth = getDaysInMonth(calMonth, calYear);
  const firstDay = getFirstDayOfMonth(calMonth, calYear);
  const monthName = new Date(calYear, calMonth).toLocaleString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); if (open) setShowCalendar(false); }}
        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-surface-card border border-surface-border rounded-lg hover:bg-surface-bg transition-colors"
      >
        <FaCalendarAlt size={12} className="text-ink-muted" />
        <span className="text-ink-primary">{currentLabel}</span>
        <FaChevronDown size={10} className="text-ink-faint" />
      </button>

      {open && !showCalendar && (
        <div className="absolute top-full mt-1 left-0 z-50 w-52 bg-surface-card border border-surface-border rounded-xl shadow-xl py-1">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handlePreset(opt)}
              className="w-full text-left px-4 py-2 text-sm text-ink-primary hover:bg-surface-bg transition-colors"
            >
              {opt.label}
            </button>
          ))}
          <div className="border-t border-surface-border my-1" />
          <button
            onClick={handleCustomClick}
            className="w-full text-left px-4 py-2 text-sm font-medium text-brand hover:bg-brand-50 transition-colors"
          >
            Custom
          </button>
        </div>
      )}

      {open && showCalendar && (
        <div className="absolute top-full mt-1 left-0 z-50 w-[300px] bg-surface-card border border-surface-border rounded-xl shadow-xl p-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button onClick={prevMonth} className="p-1 hover:bg-surface-bg rounded transition-colors">
              <FaChevronLeft size={12} className="text-ink-muted" />
            </button>
            <span className="text-sm font-semibold text-ink-primary">{monthName}</span>
            <button onClick={nextMonth} className="p-1 hover:bg-surface-bg rounded transition-colors">
              <FaChevronRight size={12} className="text-ink-muted" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-0 mb-1">
            {DAYS_OF_WEEK.map((d) => (
              <div key={d} className="text-center text-[10px] font-semibold text-ink-faint py-1">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-0">
            {Array.from({ length: firstDay }, (_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const inRange = isInRange(day);
              const start = isStart(day);
              const end = isEnd(day);
              const today = isToday(day);
              const future = isFuture(day);

              return (
                <button
                  key={day}
                  onClick={() => !future && handleDayClick(day)}
                  onMouseEnter={() => !future && rangeStart && !rangeEnd && setHoverDate(new Date(calYear, calMonth, day))}
                  disabled={future}
                  className={`
                    w-full aspect-square flex items-center justify-center text-xs rounded-md transition-all
                    ${future ? "text-ink-faint/40 cursor-not-allowed" : "cursor-pointer hover:bg-brand-50"}
                    ${inRange && !start && !end ? "bg-brand-50 text-brand" : ""}
                    ${start || end ? "bg-brand text-white font-semibold rounded-md" : ""}
                    ${today && !start && !end ? "ring-1 ring-brand/40 font-semibold" : ""}
                    ${!inRange && !start && !end && !today ? "text-ink-primary" : ""}
                  `}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Selected range display */}
          <div className="mt-3 pt-3 border-t border-surface-border">
            {rangeStart && rangeEnd ? (
              <p className="text-xs text-ink-primary text-center mb-3">
                {rangeStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                {" to "}
                {rangeEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
            ) : rangeStart ? (
              <p className="text-xs text-ink-muted text-center mb-3">Select end date...</p>
            ) : (
              <p className="text-xs text-ink-muted text-center mb-3">Select start date</p>
            )}

            <div className="flex items-center gap-2 justify-center">
              <button
                onClick={handleClear}
                className="px-4 py-1.5 text-xs font-medium text-ink-muted hover:text-ink-primary transition-colors"
              >
                Clear
              </button>
              <button
                onClick={handleApply}
                disabled={!rangeStart || !rangeEnd}
                className="px-4 py-1.5 text-xs font-medium bg-brand text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const modal = useModal();
  const { isDark } = useTheme();
  const [rangeQuery, setRangeQuery] = useState("days=30");
  const [rangeLabel, setRangeLabel] = useState("Last 30 days");
  const [overview, setOverview] = useState(null);
  const [daily, setDaily] = useState([]);
  const [statusDist, setStatusDist] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showTips, setShowTips] = useState(true);

  const fetchAll = async (q) => {
    setLoading(true);
    try {
      const [ov, dl, st, hr, cp] = await Promise.all([
        api.get(`/api/reports/overview?${q}`),
        api.get(`/api/reports/messages/daily?${q}`),
        api.get(`/api/reports/messages/status?${q}`),
        api.get(`/api/reports/messages/hourly?${q}`),
        api.get(`/api/reports/campaigns?${q}`),
      ]);
      setOverview(ov.data.data);
      setDaily(dl.data.data || []);
      setStatusDist(st.data.data || []);
      setHourly(hr.data.data || []);
      setCampaigns(cp.data.data || []);
    } catch {
      // Silently handle
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(rangeQuery); }, [rangeQuery]);

  const handleRangeChange = (selection) => {
    if (selection.from && selection.to) {
      setRangeQuery(`from=${selection.from}&to=${selection.to}`);
    } else if (selection.preset === "mtd") {
      const now = new Date();
      const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
      const to = now.toISOString().split("T")[0];
      setRangeQuery(`from=${from}&to=${to}`);
    } else if (selection.preset === "ytd") {
      const now = new Date();
      const from = `${now.getFullYear()}-01-01`;
      const to = now.toISOString().split("T")[0];
      setRangeQuery(`from=${from}&to=${to}`);
    } else {
      setRangeQuery(`days=${selection.preset}`);
    }
    setRangeLabel(selection.label);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/api/reports/export/messages?${rangeQuery}&format=csv`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `walytic-messages-report.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      modal.alert("Export failed. Please try again.", "error");
    } finally {
      setExporting(false);
    }
  };

  // Chart colors
  const brandColor = "#0171DE";
  const brandLight = isDark ? "rgba(1,113,222,0.15)" : "rgba(1,113,222,0.08)";
  const greenColor = "#10B981";
  const greenLight = isDark ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.08)";
  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
  const textColor = isDark ? "#A1A7B8" : "#6B7280";

  const chartBaseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: textColor, font: { family: "DM Sans", size: 11 }, usePointStyle: true, pointStyleWidth: 8 } },
      tooltip: {
        backgroundColor: isDark ? "#1A1D2B" : "#FFFFFF",
        titleColor: isDark ? "#E5E7EB" : "#1A1D2B",
        bodyColor: isDark ? "#A1A7B8" : "#4A5068",
        borderColor: isDark ? "#2A2E3F" : "#E8ECF0",
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        titleFont: { family: "DM Sans", weight: "600" },
        bodyFont: { family: "DM Sans" },
      },
    },
    scales: {
      x: { grid: { color: gridColor }, ticks: { color: textColor, font: { family: "DM Sans", size: 10 } } },
      y: { grid: { color: gridColor }, ticks: { color: textColor, font: { family: "DM Sans", size: 10 } }, beginAtZero: true },
    },
  };

  // Daily chart
  const dailyChart = useMemo(() => ({
    labels: daily.map((d) => {
      const date = new Date(d.date);
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }),
    datasets: [
      {
        label: "Sent",
        data: daily.map((d) => d.sent),
        borderColor: brandColor,
        backgroundColor: brandLight,
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: "Received",
        data: daily.map((d) => d.received),
        borderColor: greenColor,
        backgroundColor: greenLight,
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  }), [daily, brandColor, brandLight, greenColor, greenLight]);

  // Status doughnut
  const statusColors = {
    sent: "#3B82F6",
    delivered: "#10B981",
    read: "#8B5CF6",
    pending: "#F59E0B",
    failed: "#EF4444",
  };
  const statusChart = useMemo(() => ({
    labels: statusDist.map((s) => s.status.charAt(0).toUpperCase() + s.status.slice(1)),
    datasets: [{
      data: statusDist.map((s) => s.count),
      backgroundColor: statusDist.map((s) => statusColors[s.status] || "#9CA3AF"),
      borderWidth: 0,
      hoverOffset: 4,
    }],
  }), [statusDist]);

  // Hourly bar
  const hourlyChart = useMemo(() => ({
    labels: hourly.map((h) => `${h.hour}:00`),
    datasets: [{
      label: "Messages",
      data: hourly.map((h) => h.count),
      backgroundColor: isDark ? "rgba(1,113,222,0.4)" : "rgba(1,113,222,0.6)",
      borderRadius: 4,
      borderSkipped: false,
    }],
  }), [hourly, isDark]);

  const totalSent = overview?.sentMessages || 0;
  const totalReceived = overview?.receivedMessages || 0;
  const totalMessages = overview?.totalMessages || 0;
  const deliveryRate = totalSent > 0 ? Math.round((overview?.deliveredMessages / totalSent) * 100) : 0;

  if (loading) {
    return (
      <div className="page-content flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="mb-6 space-y-3">
        <div className="flex items-center justify-between">
          <PageHeader title="Reports" subtitle="Message analytics and campaign performance" />
          <Button variant="secondary" size="sm" onClick={handleExport} loading={exporting} className="flex-shrink-0">
            <FaDownload size={11} className="mr-1.5" />
            <span className="hidden sm:inline">Export CSV</span>
          </Button>
        </div>
        {/* Range selector dropdown with calendar */}
        <DateRangePicker onApply={handleRangeChange} currentLabel={rangeLabel} />
      </div>

      {/* Best Practices Banner */}
      {showTips && (
        <Card className="mb-5 border-brand/20">
          <CardBody>
            <div className="flex items-start justify-between mb-3 gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <FaShieldAlt size={14} className="text-brand flex-shrink-0" />
                <span className="text-sm font-semibold text-ink-primary">Best Practices</span>
                <span className="badge-brand text-2xs">Essential Tips</span>
              </div>
              <button onClick={() => setShowTips(false)} className="btn-icon flex-shrink-0"><FaTimes size={12} /></button>
            </div>
            <p className="text-xs text-ink-muted mb-3">Follow these guidelines to ensure optimal message delivery and maintain compliance:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { title: "Target Known Contacts", desc: "Message only people who expect to hear from you. Do not do cold outreach or generic spammy promos." },
                { title: "Maintain Smooth Cadence", desc: "Stay under 150-200 messages per number per day." },
                { title: "Share Value-Driven Content", desc: "Send useful updates (reminders, confirmations, alerts, notifications)." },
                { title: "Warm Up New Numbers", warn: true, desc: "Don't send messages from brand new WhatsApp numbers. First, do manual back-and-forth with saved contacts for a few weeks." },
              ].map((tip) => (
                <div key={tip.title} className="flex items-start gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${tip.warn ? "bg-yellow-500" : "bg-brand"}`} />
                  <div>
                    <p className={`text-xs font-semibold ${tip.warn ? "text-yellow-600 dark:text-yellow-400" : "text-ink-primary"}`}>{tip.title}</p>
                    <p className="text-xs text-ink-muted">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <StatCard icon={FaChartLine} label="Total Messages" value={totalMessages} />
        <StatCard icon={FaPaperPlane} label="Sent" value={totalSent} />
        <StatCard icon={FaInbox} label="Received" value={totalReceived} />
        <StatCard icon={FaCheckCircle} label="Delivery Rate" value={`${deliveryRate}%`} sub={`${overview?.deliveredMessages || 0} delivered`} />
        <StatCard icon={FaBullhorn} label="Campaigns" value={overview?.totalCampaigns || 0} sub={`${overview?.completedCampaigns || 0} completed`} />
        <StatCard icon={FaWhatsapp} label="Active Sessions" value={overview?.activeSessions || 0} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {/* Daily Message Trend — 2 cols */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-2">
              <FaChartLine size={13} className="text-brand" />
              <span className="font-semibold text-ink-primary text-sm">Daily Message Report</span>
            </div>
          </CardHeader>
          <CardBody>
            {daily.length > 0 ? (
              <div style={{ height: 280 }}>
                <Line data={dailyChart} options={{ ...chartBaseOptions, plugins: { ...chartBaseOptions.plugins, legend: { ...chartBaseOptions.plugins.legend, position: "top" } } }} />
              </div>
            ) : (
              <EmptyState icon={FaChartLine} title="No message data" description="Messages will appear here once you start sending." />
            )}
          </CardBody>
        </Card>

        {/* Status Distribution — 1 col */}
        <Card>
          <CardHeader>
            <span className="font-semibold text-ink-primary text-sm">Message Status</span>
          </CardHeader>
          <CardBody>
            {statusDist.length > 0 ? (
              <>
                <div style={{ height: 180 }} className="flex items-center justify-center">
                  <Doughnut
                    data={statusChart}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: "65%",
                      plugins: {
                        legend: { display: false },
                        tooltip: chartBaseOptions.plugins.tooltip,
                      },
                    }}
                  />
                </div>
                <div className="mt-4 space-y-2">
                  {statusDist.map((s) => (
                    <div key={s.status} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: statusColors[s.status] || "#9CA3AF" }} />
                        <span className="text-xs text-ink-secondary capitalize">{s.status}</span>
                      </div>
                      <span className="text-xs font-semibold text-ink-primary">{s.count}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <EmptyState icon={FaCheckCircle} title="No data" description="Status breakdown will appear here." />
            )}
          </CardBody>
        </Card>
      </div>

      {/* Second Row: Hourly + Campaigns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Peak Hours */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FaClock size={13} className="text-brand" />
              <span className="font-semibold text-ink-primary text-sm">Peak Hours</span>
            </div>
          </CardHeader>
          <CardBody>
            {hourly.some((h) => h.count > 0) ? (
              <div style={{ height: 220 }}>
                <Bar
                  data={hourlyChart}
                  options={{
                    ...chartBaseOptions,
                    plugins: { ...chartBaseOptions.plugins, legend: { display: false } },
                    scales: {
                      ...chartBaseOptions.scales,
                      x: { ...chartBaseOptions.scales.x, ticks: { ...chartBaseOptions.scales.x.ticks, maxTicksLimit: 12 } },
                    },
                  }}
                />
              </div>
            ) : (
              <EmptyState icon={FaClock} title="No hourly data" description="Hourly activity will appear as you send messages." />
            )}
          </CardBody>
        </Card>

        {/* Campaign Performance */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FaBullhorn size={13} className="text-brand" />
              <span className="font-semibold text-ink-primary text-sm">Campaign Performance</span>
            </div>
          </CardHeader>
          {campaigns.length > 0 ? (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Campaign</Th>
                    <Th>Delivered</Th>
                    <Th>Rate</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.slice(0, 5).map((c, i) => {
                    const rate = c.total > 0 ? Math.round((c.delivered / c.total) * 100) : 0;
                    return (
                      <tr key={c._id || i}>
                        <Td>
                          <span className="text-sm font-medium text-ink-primary">{c.name || "Untitled"}</span>
                          <div className="text-2xs text-ink-faint">{new Date(c.createdAt).toLocaleDateString()}</div>
                        </Td>
                        <Td className="text-sm">{c.delivered}/{c.total}</Td>
                        <Td>
                          <div className="flex items-center gap-1.5">
                            <div className="w-12 h-1.5 bg-surface-bg rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-brand" style={{ width: `${rate}%` }} />
                            </div>
                            <span className="text-xs text-ink-muted">{rate}%</span>
                          </div>
                        </Td>
                        <Td>
                          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                            c.status === "completed" ? "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400" :
                            c.status === "processing" ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400" :
                            "bg-surface-bg text-ink-muted"
                          }`}>
                            {c.status}
                          </span>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          ) : (
            <CardBody>
              <EmptyState icon={FaBullhorn} title="No campaigns" description="Campaign stats will show here." />
            </CardBody>
          )}
        </Card>
      </div>

      {/* Verification Stats */}
      {overview && (overview.totalVerifications > 0) && (
        <Card>
          <CardHeader>
            <span className="font-semibold text-ink-primary text-sm">Number Verification Summary</span>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-lg bg-surface-bg">
                <p className="text-2xl font-bold text-ink-primary">{overview.totalVerifications}</p>
                <p className="text-xs text-ink-muted mt-1">Total Verified</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{overview.validNumbers}</p>
                <p className="text-xs text-ink-muted mt-1">Valid Numbers</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-red-50 dark:bg-red-900/20">
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{overview.totalVerifications - overview.validNumbers}</p>
                <p className="text-xs text-ink-muted mt-1">Invalid Numbers</p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
